import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/context/LanguageContext';
import { motion } from 'framer-motion';
import { Check, Ruler, UserCheck, Building2, FileCheck, Package, CalendarCheck } from 'lucide-react';

const STEPS = [
  { key: 'measurements', icon: Ruler },
  { key: 'techVisit', icon: UserCheck },
  { key: 'showroom', icon: Building2 },
  { key: 'agreement', icon: FileCheck },
  { key: 'production', icon: Package },
  { key: 'installation', icon: CalendarCheck },
];

const OrderLayout = ({ currentStep, children, orderNumber }) => {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();

  return (
    <div className="min-h-screen py-24 md:py-32">
      <div className="container-custom max-w-4xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <span className="label-text mb-3 block">{t('order.journey')}</span>
          {orderNumber && (
            <p className="text-sm text-charcoal/50 mb-1">
              {t('order.orderNumber')}: <span className="font-semibold text-gold">{orderNumber}</span>
            </p>
          )}
          <p className="text-charcoal/50 text-sm">
            {t('order.step')} {currentStep} {t('order.of')} 6
          </p>
        </motion.div>

        {/* Step Progress */}
        <div className="flex items-center mb-10 px-2">
          {STEPS.map((step, index) => {
            const StepIcon = step.icon;
            const stepNum = index + 1;
            const isCompleted = stepNum < currentStep;
            const isActive = stepNum === currentStep;

            return (
              <React.Fragment key={stepNum}>
                <div className="flex flex-col items-center gap-1 flex-shrink-0">
                  <div
                    className={`w-9 h-9 md:w-11 md:h-11 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                      isCompleted
                        ? 'border-gold bg-gold'
                        : isActive
                        ? 'border-gold bg-white shadow-lg shadow-gold/20'
                        : 'border-charcoal/15 bg-white/60'
                    }`}
                  >
                    {isCompleted ? (
                      <Check size={16} className="text-white font-bold" strokeWidth={3} />
                    ) : (
                      <StepIcon
                        size={14}
                        className={isActive ? 'text-gold' : 'text-charcoal/25'}
                      />
                    )}
                  </div>
                  <span
                    className={`hidden md:block text-xs font-medium text-center max-w-[60px] leading-tight ${
                      isActive ? 'text-gold' : isCompleted ? 'text-charcoal/50' : 'text-charcoal/25'
                    }`}
                  >
                    {t(`order.steps.${step.key}.title`)}
                  </span>
                </div>
                {index < 5 && (
                  <div
                    className={`flex-1 h-0.5 mx-1 transition-all duration-500 ${
                      stepNum < currentStep ? 'bg-gold' : 'bg-charcoal/10'
                    }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step title */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: currentLang === 'ar' ? -20 : 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-2xl md:text-3xl font-bold text-charcoal">
            {t(`order.steps.${STEPS[currentStep - 1]?.key}.title`)}
          </h1>
          <p className="text-charcoal/55 mt-2">
            {t(`order.steps.${STEPS[currentStep - 1]?.key}.desc`)}
          </p>
        </motion.div>

        {/* Content Card */}
        <motion.div
          key={`content-${currentStep}`}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass p-8 md:p-10"
        >
          {children}
        </motion.div>
      </div>
    </div>
  );
};

export default OrderLayout;
